<template>
  <div class="dvh">
    <!-- SECCIÓN 1: Identidad (centrado) -->
    <div class="dvh-identity">
      <img
        v-if="business.logo"
        :src="business.logo"
        class="dvh-logo"
        alt="Logo"
      />
      <p class="dvh-doc-type">{{ docType }}</p>
      <p class="dvh-doc-number">N° {{ docNumber }}</p>
      <p v-if="business.name" class="dvh-business-name">{{ business.name }}</p>
      <p v-if="business.ruc" class="dvh-business-ruc">
        RUC: {{ business.ruc }}
      </p>
    </div>

    <div class="dvh-divider"></div>

    <!-- SECCIÓN 2: Metadatos en grid 2x2 -->
    <div class="dvh-meta-grid">
      <div class="dvh-meta-cell">
        <span class="dvh-meta-label">Fecha</span>
        <span class="dvh-meta-value">{{ date || "—" }}</span>
      </div>
      <div class="dvh-meta-cell">
        <span class="dvh-meta-label">Estado</span>
        <StatusReadDocumentsComponent v-if="status" :status="status" />
        <span v-else class="dvh-meta-value">—</span>
      </div>
      <div v-for="(item, i) in metaItems" :key="i" class="dvh-meta-cell">
        <span class="dvh-meta-label">{{ item.label }}</span>
        <span class="dvh-meta-value">{{ item.value || "—" }}</span>
      </div>
    </div>

    <div class="dvh-divider"></div>

    <!-- SECCIÓN 3: Cliente -->
    <div class="dvh-client">
      <span class="dvh-meta-label">Cliente</span>
      <p class="dvh-client-name">{{ client.name }}</p>
      <p v-if="client.document" class="dvh-client-meta">
        {{ client.document }}
      </p>
      <p v-if="client.email" class="dvh-client-meta">{{ client.email }}</p>
    </div>

    <!-- SECCIÓN 4: Autorización SRI -->
    <template v-if="accessKey">
      <div class="dvh-divider"></div>
      <div class="dvh-sri-section">
        <p class="dvh-sri-title">Autorización SRI</p>
        <div v-if="signMode" class="dvh-sri-row">
          <span class="dvh-meta-label">Ambiente</span>
          <span
            :class="['dvh-sri-env', signMode === 2 ? 'env-prod' : 'env-dev']"
          >
            {{ signMode === 2 ? "Producción" : "Desarrollo" }}
          </span>
        </div>
        <div class="dvh-sri-row">
          <span class="dvh-meta-label">Número / Clave de acceso</span>
          <div class="dvh-sri-key-row">
            <span class="dvh-sri-code">{{ accessKey }}</span>
            <button class="dvh-copy-btn" @click="copyKey" :title="copied ? 'Copiado' : 'Copiar clave'">
              <i :class="copied ? 'el-icon-check' : 'el-icon-copy-document'"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="dvh-divider"></div>
    </template>
  </div>
</template>

<script>
import StatusReadDocumentsComponent from "../../components/StatusReadDocuments";

export default {
  name: "DocumentViewHeader",
  components: { StatusReadDocumentsComponent },
  data() {
    return { copied: false };
  },
  methods: {
    copyKey() {
      if (!this.accessKey) return;
      navigator.clipboard.writeText(this.accessKey).then(() => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      }).catch(() => {
        // Fallback para navegadores sin clipboard API
        const el = document.createElement("textarea");
        el.value = this.accessKey;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      });
    }
  },
  props: {
    docType: { type: String, required: true },
    docNumber: { type: [String, Number], default: "" },
    business: {
      type: Object,
      default: () => ({ logo: null, name: "", ruc: "", address: "" })
    },
    date: { type: String, default: null },
    status: { type: String, default: null },
    client: {
      type: Object,
      default: () => ({ name: "", document: "", email: "" })
    },
    accessKey: { type: String, default: null },
    signMode: { default: null },
    // Array de { label, value } para el grid de metadatos adicionales
    metaItems: { type: Array, default: () => [] }
  }
};
</script>

<style lang="scss" scoped>
$primary: #31355c;
$gray-2: #6b7280;
$gray-3: #9ca3af;
$border: rgba(0, 0, 0, 0.06);

.dvh {
  padding: 0;
}

// Sección 1
.dvh-identity {
  text-align: center;
  padding: 16px 0 12px;
}
.dvh-logo {
  display: block;
  max-height: 40px;
  max-width: 120px;
  object-fit: contain;
  margin: 0 auto 8px;
}
.dvh-doc-type {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: $gray-3;
  margin: 0 0 4px;
}
.dvh-doc-number {
  font-size: 16px;
  font-weight: 500;
  color: $primary;
  margin: 0 0 2px;
}
.dvh-business-name {
  font-size: 12px;
  color: $gray-2;
  margin: 0 0 2px;
}
.dvh-business-ruc {
  font-size: 12px;
  color: $gray-2;
  margin: 0;
}

.dvh-divider {
  border: none;
  border-bottom: 1px solid $border;
  margin: 12px 0;
}

// Sección 2: grid de metadatos
.dvh-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
  padding: 4px 0;
}
.dvh-meta-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
}
.dvh-meta-label {
  font-size: 11px;
  color: $gray-3;
  display: block;
}
.dvh-meta-value {
  font-size: 13px;
  font-weight: 500;
  color: $primary;
}

// Sección 3: cliente
.dvh-client {
  padding: 4px 0;
}
.dvh-client-name {
  font-size: 14px;
  font-weight: 500;
  color: $gray-2;
  margin: 2px 0;
}
.dvh-client-meta {
  font-size: 12px;
  color: $gray-2;
  margin: 0;
}

// Sección 4: SRI
.dvh-sri-section {
  padding: 4px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.dvh-sri-title {
  font-size: 13px;
  font-weight: 600;
  color: $gray-2;
  margin: 0;
}
.dvh-sri-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
}
.dvh-sri-key-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
}
.dvh-sri-code {
  font-size: 11px;
  font-family: monospace;
  word-break: break-all;
  color: $gray-2;
  line-height: 1.5;
  flex: 1;
}
.dvh-copy-btn {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: $gray-3;
  font-size: 13px;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1;
  transition: color 0.15s, background 0.15s;
  &:hover {
    color: $primary;
    background: rgba(0,0,0,0.04);
  }
}
.dvh-sri-env {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  display: inline-block;

  &.env-prod {
    background: #e4fff4;
    color: #1d9e75;
  }
  &.env-dev {
    background: #fff8e6;
    color: #ba7517;
  }
}
</style>
